/**
 * Which fabrics and elements a made-to-measure model offers, with the pairs
 * that are mutually compatible or incompatible. Admin only.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faCaretUp,
  faCircleNotch,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM, ROLES } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { snackbar } from '@/shared/ui/snackbar';
import { updateDmsItem } from '../api';
import { dmsLinkItemsQueryOptions } from './api';
import {
  buildDmsTree,
  isDmsProductLineEntry,
  type DmsFlatNode,
  type DmsProductLineEntry,
} from '../dms-tree';
import { DmsProductLineTree } from '../DmsProductLineTree';
import {
  buildCatalogue,
  displayMutualIncompatibilities,
  groupedModelElements,
  incompLabel,
  modelFabrics,
  modelHasItem,
  modelHasItemOfType,
  modelToDbData,
  noSpaces,
  pageTitle,
  pairOptions,
  resolveModel,
  unselectedItems,
  type EditableModel,
  type ItemPair,
  linkDataSchema,
  type LinkItem,
} from './link-logic';
import { Select } from '@/shared/ui/Select';
import { kebabCase } from '@/shared/lib/kebab-case';

type PairField = 'compA' | 'compB' | 'incompA' | 'incompB';

interface ItemOption {
  label: string;
  value: LinkItem;
}

/** The item of the option with the given id, null when none (e.g. the placeholder). */
const optionValue = (
  options: readonly ItemOption[],
  id: string,
): LinkItem | null =>
  options.find((option) => option.value.id === id)?.value ?? null;

export function LinkPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    // Non-admins are sent to `/dms/main`, a route that does not exist (see migration.md).
    if (user && !isAdmin) hardNavigate('/dms/main');
  }, [user, isAdmin]);

  const query = useQuery({
    ...dmsLinkItemsQueryOptions(linkDataSchema),
    enabled: isAdmin,
  });
  useEffect(() => {
    if (query.isError) snackbar('Failed to load items, please try again later');
  }, [query.isError]);

  const catalogue = useMemo(
    () => buildCatalogue(query.data ?? []),
    [query.data],
  );
  const tree = useMemo(
    () =>
      buildDmsTree(catalogue.models, (item) => String(item.data.model ?? '')),
    [catalogue.models],
  );

  const [productLine, setProductLine] = useState<string>('');
  const [model, setModel] = useState<EditableModel | null>(null);
  const [selected, setSelected] = useState<Record<string, LinkItem | null>>({});
  const [searchFabric, setSearchFabric] = useState('');
  const [searchElement, setSearchElement] = useState<Record<string, string>>(
    {},
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const fabrics = useMemo(
    () =>
      model
        ? catalogue.allFabrics.filter(
            (f) => f.data.productLine === model.item.data.productLine,
          )
        : [],
    [catalogue.allFabrics, model],
  );

  const save = useMutation({
    mutationFn: (current: EditableModel) =>
      updateDmsItem(current.item.id, modelToDbData(current), linkDataSchema),
    onSuccess: (saved) => {
      // The updated model goes back in its lists.
      queryClient.setQueryData(
        dmsLinkItemsQueryOptions(linkDataSchema).queryKey,
        (items: LinkItem[] | undefined) =>
          items?.map((item) => (item.id === saved.id ? saved : item)),
      );
      snackbar('Successfully saved');
    },
    onError: () => snackbar('Failed to save item, please try again later'),
  });
  const isSaving = save.isPending;

  const onNodeClick = (node: DmsFlatNode<DmsProductLineEntry | LinkItem>) => {
    if (isSaving) return;
    if (node.level === 0 && isDmsProductLineEntry(node.data)) {
      const { value } = node.data;
      if (value && value !== productLine) setProductLine(value);
    }
    if (node.level === 1 && !isDmsProductLineEntry(node.data)) {
      const clicked = node.data;
      if (model && clicked.id === model.item.id) return;
      const found = catalogue.models.find((m) => m.id === clicked.id);
      if (!found) return;
      setSelected({});
      setSearchFabric('');
      setSearchElement({});
      setModel(resolveModel(found, catalogue.items));
    }
  };

  const update = (change: (current: EditableModel) => EditableModel) =>
    setModel((current) => (current ? change(current) : current));

  const addAll = (items: LinkItem[]) =>
    update((m) => ({ ...m, options: m.options.concat(items) }));
  const addSelected = (key: string) => {
    const item = selected[key];
    if (!item) return;
    update((m) => ({ ...m, options: [...m.options, item] }));
    setSelected((prev) => ({ ...prev, [key]: null }));
  };
  const removeItem = (item: LinkItem) =>
    update((m) => ({
      ...m,
      options: m.options.filter((o) => o.id !== item.id),
    }));
  const removeType = (type: string, subtype?: string) =>
    update((m) => ({
      ...m,
      options: m.options.filter(
        (o) => !(o.type === type && (!subtype || o.data.type === subtype)),
      ),
    }));
  const addPair = (kind: 'compatibilities' | 'incompatibilities') => {
    const [a, b] =
      kind === 'compatibilities'
        ? [selected.compA, selected.compB]
        : [selected.incompA, selected.incompB];
    if (!a || !b || a === b) return;
    update((m) => ({ ...m, [kind]: [...m[kind], [a, b] as ItemPair] }));
    setSelected((prev) =>
      kind === 'compatibilities'
        ? { ...prev, compA: null, compB: null }
        : { ...prev, incompA: null, incompB: null },
    );
  };
  const removePair = (
    kind: 'compatibilities' | 'incompatibilities',
    pair: ItemPair,
  ) => update((m) => ({ ...m, [kind]: m[kind].filter((p) => p !== pair) }));

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!isAdmin) return null;

  const grouped = model ? groupedModelElements(model, searchElement) : {};
  const showPairs = displayMutualIncompatibilities(model);

  const section = (
    id: string,
    title: string,
    testId: string,
    headerRight: ReactNode,
    options: ReactNode,
    footer: ReactNode,
  ) => (
    <div className="section" key={id} data-testid={testId}>
      <div className="section-header">
        {title}
        <div className="flex items-center">
          {headerRight}
          <button
            type="button"
            aria-expanded={!collapsed.has(id)}
            aria-controls={id}
            aria-label={`Toggle ${title}`}
            data-testid={`${testId}-toggle-button`}
            onClick={() => toggle(id)}
          >
            <FontAwesomeIcon
              icon={collapsed.has(id) ? faCaretUp : faCaretDown}
            />
          </button>
        </div>
      </div>
      <div className="option-list" id={id} hidden={collapsed.has(id)}>
        {options}
      </div>
      {footer}
    </div>
  );

  const selectItem = (
    key: string,
    options: readonly ItemOption[],
    id: string,
  ) => setSelected((prev) => ({ ...prev, [key]: optionValue(options, id) }));

  const itemSelect = (
    key: string,
    options: ItemOption[],
    label: string,
    testId: string,
    extraClass = '',
  ) => (
    <div className={`field field-selector mt-0 ${extraClass}`}>
      <Select
        aria-label={label}
        testId={testId}
        placeholder="--Select an item--"
        options={options.map((option) => ({
          value: option.value.id,
          label: option.label,
        }))}
        value={selected[key]?.id ?? ''}
        onChange={(id) => selectItem(key, options, id)}
      />
    </div>
  );

  const midSection = (
    id: string,
    title: string,
    items: LinkItem[],
    labelOf: (item: LinkItem) => string,
    onSearch: (value: string) => void,
    onRemoveAll: () => void,
    unselected: ItemOption[],
    selectKey: string,
  ) => {
    const slug = kebabCase(title);
    return section(
      id,
      title,
      `mtm-link-section-${slug}`,
      <>
        <input
          className="field"
          style={{ marginLeft: 5 }}
          type="search"
          placeholder="Search..."
          aria-label={`Search ${title}`}
          data-testid={`mtm-link-${slug}-search-input`}
          onChange={(event) => onSearch(event.target.value.toLowerCase())}
        />
        <button
          type="button"
          aria-label={`Remove all ${title}`}
          data-testid={`mtm-link-${slug}-remove-all-button`}
          onClick={onRemoveAll}
        >
          <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
        </button>
      </>,
      items.map((item) => (
        <div
          className="option mt-1"
          key={item.id}
          data-testid="mtm-link-item"
          data-value={item.id}
        >
          {labelOf(item)}
          <button
            type="button"
            className="action-icon ml-2"
            aria-label={`Remove ${labelOf(item)}`}
            data-testid="mtm-link-item-remove-button"
            onClick={() => removeItem(item)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      )),
      unselected.length > 0 && (
        <div className="section-footer">
          {itemSelect(
            selectKey,
            unselected,
            `Add ${title}`,
            `mtm-link-add-${slug}`,
          )}
          <button
            type="button"
            className="button button-form"
            data-testid={`mtm-link-add-${slug}-button`}
            disabled={!selected[selectKey]}
            onClick={() => addSelected(selectKey)}
          >
            Add
          </button>
        </div>
      ),
    );
  };

  const pairsTable = (
    title: string,
    kind: 'compatibilities' | 'incompatibilities',
    fieldA: PairField,
    fieldB: PairField,
  ) => {
    if (!model) return null;
    const a = selected[fieldA] ?? null;
    const b = selected[fieldB] ?? null;
    const same = !!a && a === b;
    const pairId = `mtm-link-${kind === 'compatibilities' ? 'compatibility' : 'incompatibility'}`;
    return (
      <div data-testid={`${pairId}-table`}>
        <h3 className="mb-4 mt-6">{title}</h3>
        <div className="table-container">
          <div
            className="items-container"
            style={{ maxHeight: 420, overflow: 'visible' }}
          >
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Element A</th>
                  <th style={{ width: '40%' }}>Element B</th>
                  <th style={{ width: '20%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {model[kind].map((pair, index) => (
                  <tr
                    key={`${pair[0].id}-${pair[1].id}-${index}`}
                    data-testid={`${pairId}-row`}
                  >
                    <td
                      style={{ width: '40%' }}
                      className={modelHasItem(model, pair[0]) ? '' : 'red'}
                      data-testid={`${pairId}-row-a`}
                    >
                      {incompLabel(pair[0])}
                    </td>
                    <td
                      style={{ width: '40%' }}
                      className={modelHasItem(model, pair[1]) ? '' : 'red'}
                      data-testid={`${pairId}-row-b`}
                    >
                      {incompLabel(pair[1])}
                    </td>
                    <td style={{ width: '20%' }} className="edit-action">
                      <button
                        type="button"
                        className="action-icon ml-2"
                        aria-label={`Remove pair ${incompLabel(pair[0])} / ${incompLabel(pair[1])}`}
                        data-testid={`${pairId}-row-remove-button`}
                        onClick={() => removePair(kind, pair)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ width: '40%', overflow: 'visible' }}>
                    {itemSelect(
                      fieldA,
                      pairOptions(model, b),
                      `${title} element A`,
                      `${pairId}-a`,
                      same ? 'red' : '',
                    )}
                  </td>
                  <td style={{ width: '40%', overflow: 'visible' }}>
                    {itemSelect(
                      fieldB,
                      pairOptions(model, a),
                      `${title} element B`,
                      `${pairId}-b`,
                      same ? 'red' : '',
                    )}
                  </td>
                  <td style={{ width: '20%' }} className="edit-action">
                    <button
                      type="button"
                      className="button button-form"
                      data-testid={`${pairId}-add-button`}
                      disabled={!a || !b || same}
                      onClick={() => addPair(kind)}
                    >
                      Add
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container summary">
      <TopMenu platform={PLATFORM.DMS} />
      <fieldset disabled={isSaving} style={{ overflow: 'auto' }}>
        <div className="bs-row dms-link my-12" style={{ flex: 1 }}>
          <div className="col-span-2 col-left">
            <DmsProductLineTree
              data={tree}
              selectedParent={productLine}
              selectedNode={String(model?.item.data.model ?? '')}
              onNodeClick={onNodeClick}
            />
          </div>
          <div className="col-span-8 col-mid">
            <div className="mb-4 flex justify-between">
              <h3 className="mb-0" data-testid="mtm-link-title">
                {pageTitle(model)}
              </h3>
              {model && (
                <button
                  type="button"
                  className="button button-form"
                  data-testid="mtm-link-save-button"
                  onClick={() => save.mutate(model)}
                >
                  Save Changes
                </button>
              )}
            </div>
            {model &&
              modelFabrics(model).length > 0 &&
              midSection(
                'collapse-mid-fabric',
                'FABRIC',
                modelFabrics(model, searchFabric),
                (item) => String(item.data.code ?? ''),
                setSearchFabric,
                () => removeType('fabric'),
                unselectedItems(model, fabrics).map((item) => ({
                  label: String(item.data.code ?? ''),
                  value: item,
                })),
                'fabric',
              )}
            {model &&
              Object.entries(grouped).map(([type, items]) =>
                midSection(
                  `collapse-mid-${noSpaces(type)}`,
                  type,
                  items,
                  (item) => String(item.data.name ?? ''),
                  (value) =>
                    setSearchElement((prev) => ({ ...prev, [type]: value })),
                  () => removeType('element', type),
                  unselectedItems(
                    model,
                    catalogue.elementsByType[type] ?? [],
                  ).map((item) => ({
                    label: String(item.data.name ?? ''),
                    value: item,
                  })),
                  type,
                ),
              )}
            {showPairs &&
              pairsTable(
                'Mutual Incompatibilities',
                'incompatibilities',
                'incompA',
                'incompB',
              )}
            {showPairs &&
              pairsTable(
                'Mutual Compatibilities',
                'compatibilities',
                'compA',
                'compB',
              )}
          </div>
          {model && (
            <div className="col-span-2 col-right border-l">
              {!modelHasItemOfType(model, 'fabric') &&
                fabrics.length > 0 &&
                section(
                  'collapse-right-fabrics',
                  'FABRIC',
                  'mtm-link-catalogue-fabric',
                  null,
                  fabrics.map((item) => (
                    <div
                      className="option mt-1"
                      key={item.id}
                      data-testid="mtm-link-catalogue-item"
                      data-value={item.id}
                    >
                      {String(item.data.code ?? '')}
                    </div>
                  )),
                  <div className="section-footer">
                    <button
                      type="button"
                      className="button button-form"
                      data-testid="mtm-link-add-all-fabric-button"
                      onClick={() => addAll(fabrics)}
                    >
                      Add to Model
                    </button>
                  </div>,
                )}
              {Object.entries(catalogue.elementsByType).map(
                ([type, items]) =>
                  !modelHasItemOfType(model, 'element', type) &&
                  items.length > 0 &&
                  section(
                    `collapse-right-${noSpaces(type)}`,
                    type,
                    `mtm-link-catalogue-${kebabCase(type)}`,
                    null,
                    items.map((item) => (
                      <div
                        className="option mt-1"
                        key={item.id}
                        data-testid="mtm-link-catalogue-item"
                        data-value={item.id}
                      >
                        {String(item.data.name ?? '')}
                      </div>
                    )),
                    <div className="section-footer">
                      <button
                        type="button"
                        className="button button-form"
                        data-testid={`mtm-link-add-all-${kebabCase(type)}-button`}
                        onClick={() => addAll(items)}
                      >
                        Add to Model
                      </button>
                    </div>,
                  ),
              )}
            </div>
          )}
        </div>
      </fieldset>
      {(query.isFetching || isSaving) && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="loading-icon"
          aria-label="Loading"
        />
      )}
    </div>
  );
}
